import React, { useState, useEffect } from 'react';
import { Sparkles, X, Lightbulb, RefreshCw, BookOpen } from 'lucide-react';
import NeuroInformedBadge from '../NeuroInformedBadge';
import HelperIcon from '../HelperIcon';
import { helperContent } from '../../content/helper-content';
import { apiAIFetch } from '../../api';
import '../../styles/whiteboard-session.css';

/**
 * SessionOpenerPanel Component
 *
 * AI-generated session opening suggestions including:
 * - Hook based on student interests
 * - Review question from previous session
 * - Visual bridge concept
 *
 * Each suggestion includes a NeuroInformedBadge showing the technique used.
 */

const SessionOpenerPanel = ({
  isOpen,
  onClose,
  sessionContext,
  onSelectSuggestion
}) => {
  const [suggestions, setSuggestions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset suggestions when panel opens so generation is always explicit user action.
  useEffect(() => {
    if (isOpen) {
      setSuggestions(null);
      setError(null);
    }
  }, [isOpen]);

  const fetchSuggestions = async () => {
    if (!sessionContext) {
      setSuggestions({
        hook: {
          type: 'Hook',
          text: "Start with an intriguing question or puzzle related to today's topic to capture attention.",
          technique: 'Alerting Cue'
        },
        review: {
          type: 'Review',
          text: "What do you remember from our last session? Let's connect it to what we'll learn today.",
          technique: 'Spaced Retrieval'
        },
        bridge: {
          type: 'Bridge',
          text: "Can you think of how today's topic might relate to something you already know?",
          technique: 'Connection Building'
        }
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiAIFetch('/api/whiteboard/session-opener', 'whiteboard.session_opener.generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: {
            sessionId: sessionContext.id,
            summary: sessionContext.summary || sessionContext,
            studentProfile: sessionContext.student_profile || sessionContext.studentProfile
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate suggestions');
      }

      const data = await response.json();
      setSuggestions(data.suggestions);
    } catch (err) {
      console.error('Error fetching session opener suggestions:', err);
      setError('Could not generate suggestions. Using defaults.');
      // Fallback suggestions
      setSuggestions({
        hook: {
          type: 'Hook',
          text: "Start with an intriguing question or puzzle related to today's topic to capture attention.",
          technique: 'Alerting Cue'
        },
        review: {
          type: 'Review',
          text: "What do you remember from our last session? Let's connect it to what we'll learn today.",
          technique: 'Spaced Retrieval'
        },
        bridge: {
          type: 'Bridge',
          text: "Can you think of how today's topic might relate to something you already know?",
          technique: 'Connection Building'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
    }
    onClose();
  };

  const handleRefresh = () => {
    setSuggestions(null);
    fetchSuggestions();
  };

  if (!isOpen) return null;

  return (
    <div className="session-opener-panel" onClick={onClose}>
      <div className="session-opener-panel__modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="session-opener-panel__header">
          <h2 className="session-opener-panel__title">
            <span className="session-opener-panel__title-icon"><Sparkles size={24} /></span>
            Session Opener
          </h2>
          <button
            className="session-opener-panel__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="session-opener-panel__content">
          <p className="session-opener-panel__intro">
            Start your session with brain-friendly openers. These suggestions activate the
            Alerting Network and prime the brain for learning.
            <HelperIcon
              title={helperContent.openingPhase.title}
              content={helperContent.openingPhase.content}
              size="small"
            />
          </p>

          {isLoading ? (
            <div className="session-opener-panel__loading">
              <div className="session-opener-panel__loading-spinner" />
              <p>Generating personalized suggestions...</p>
            </div>
          ) : (
            <>
              {!suggestions && (
                <button
                  className="session-opener-panel__refresh"
                  onClick={fetchSuggestions}
                >
                  <Sparkles size={14} /> Generate suggestions
                </button>
              )}
              {error && (
                <p className="session-opener-panel__error">{error}</p>
              )}

              <div className="session-opener-panel__suggestions">
                {suggestions?.hook && (
                  <div
                    className="session-opener-panel__suggestion"
                    onClick={() => handleSuggestionClick(suggestions.hook)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="session-opener-panel__suggestion-header">
                      <Lightbulb size={16} />
                      <span className="session-opener-panel__suggestion-type">
                        {suggestions.hook.type || 'Hook'}
                      </span>
                    </div>
                    <p className="session-opener-panel__suggestion-text">
                      {suggestions.hook.text}
                    </p>
                    <div className="session-opener-panel__suggestion-badge">
                      <NeuroInformedBadge
                        technique={suggestions.hook.technique}
                        size="small"
                        variant="subtle"
                      />
                    </div>
                  </div>
                )}

                {suggestions?.review && (
                  <div
                    className="session-opener-panel__suggestion"
                    onClick={() => handleSuggestionClick(suggestions.review)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="session-opener-panel__suggestion-header">
                      <RefreshCw size={16} />
                      <span className="session-opener-panel__suggestion-type">
                        {suggestions.review.type || 'Review'}
                      </span>
                    </div>
                    <p className="session-opener-panel__suggestion-text">
                      {suggestions.review.text}
                    </p>
                    <div className="session-opener-panel__suggestion-badge">
                      <NeuroInformedBadge
                        technique={suggestions.review.technique}
                        size="small"
                        variant="subtle"
                      />
                    </div>
                  </div>
                )}

                {suggestions?.bridge && (
                  <div
                    className="session-opener-panel__suggestion"
                    onClick={() => handleSuggestionClick(suggestions.bridge)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="session-opener-panel__suggestion-header">
                      <BookOpen size={16} />
                      <span className="session-opener-panel__suggestion-type">
                        {suggestions.bridge.type || 'Bridge'}
                      </span>
                    </div>
                    <p className="session-opener-panel__suggestion-text">
                      {suggestions.bridge.text}
                    </p>
                    <div className="session-opener-panel__suggestion-badge">
                      <NeuroInformedBadge
                        technique={suggestions.bridge.technique}
                        size="small"
                        variant="subtle"
                      />
                    </div>
                  </div>
                )}
              </div>

              <button
                className="session-opener-panel__skip"
                onClick={onClose}
              >
                Skip and start session
              </button>

              {sessionContext && (
                <button
                  className="session-opener-panel__refresh"
                  onClick={handleRefresh}
                  style={{ marginTop: '8px' }}
                >
                  <RefreshCw size={14} /> Generate new suggestions
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionOpenerPanel;
